import Link from 'next/link';
import MarketingLayout from '../components/MarketingLayout';

const PAYWALL_REASONS = {
  no_subscription: 'Start a 14-day free trial to use the planner.',
  canceled: 'Your subscription was canceled. Re-subscribe below to keep going.',
  unpaid: 'Your last payment did not go through. Re-subscribe to restore access.',
  incomplete: 'We did not receive a completed payment. Try the checkout again.',
  trial_expired: 'Your trial ended. Re-subscribe below to keep your planner.',
};

export async function getServerSideProps(context) {
  const reason = context.query.reason;
  return {
    props: {
      reason: typeof reason === 'string' ? reason : null,
    },
  };
}

const BENEFITS = [
  'Daily 10x goal setting',
  'Daily anchor quotes',
  'Target tracking',
  'Win logging',
  'Full history access',
  'Any device',
];

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      className="h-6 w-6 text-green-500"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function Pricing({ reason }) {
  const reasonMessage = reason ? PAYWALL_REASONS[reason] : null;

  return (
    <MarketingLayout
      title="Pricing - 10X Planner App"
      description="One plan, one price. 14-day free trial, then $15/month. Cancel anytime."
      canonical="/pricing"
    >
      <div className="pb-12">
        {reasonMessage && (
          <div className="mx-auto mt-6 max-w-2xl rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 border border-amber-200">
            {reasonMessage}
          </div>
        )}
        <div className="pt-8 px-4 sm:px-6 lg:px-8 lg:pt-12">
          <div className="text-center">
            <h2 className="text-lg leading-6 font-semibold text-gray-400 uppercase tracking-wider">
              Pricing
            </h2>
            <p className="mt-1 text-3xl leading-9 font-extrabold text-gray-800 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
              Simple, transparent pricing
            </p>
          </div>
        </div>

        <div className="mt-16 lg:mt-20 lg:pb-20">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative lg:grid lg:grid-cols-7">
              <div className="mt-10 max-w-lg mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-start-3 lg:col-end-6 lg:row-start-1 lg:row-end-4">
                <div className="relative z-10 rounded-lg shadow-xl">
                  <div className="pointer-events-none absolute inset-0 rounded-lg border-2 border-blue-800" />
                  <div className="absolute inset-x-0 top-0 transform translate-y-px">
                    <div className="flex justify-center transform -translate-y-1/2">
                      <span className="inline-flex rounded-full bg-blue-800 px-4 py-1 text-sm leading-5 font-semibold tracking-wider uppercase text-white">
                        14-day free trial
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-t-lg px-6 pt-12 pb-10">
                    <div>
                      <h3
                        className="text-center text-3xl leading-9 font-semibold text-gray-900 sm:-mx-6"
                        id="tier-pro-annual"
                      >
                        10X Planner
                      </h3>
                      <p className="mt-2 text-center text-sm text-gray-500">
                        Free for 14 days, then:
                      </p>
                      <div className="mt-4 md:flex items-center justify-center">
                        <div className="flex items-center justify-center">
                          <span className="px-3 flex items-start text-6xl leading-none tracking-tight text-gray-900">
                            <span className="mt-2 mr-2 text-4xl font-medium">$</span>
                            <span className="font-extrabold">15</span>
                          </span>
                          <span className="text-2xl leading-8 font-medium text-gray-500">/month</span>
                        </div>
                      </div>
                      <p className="mt-3 text-center text-sm text-gray-500">
                        Cancel anytime. We don&rsquo;t charge until your trial ends.
                      </p>
                    </div>
                  </div>
                  <div className="border-t-2 border-gray-100 rounded-b-lg pt-10 pb-8 px-6 bg-gray-50 sm:px-10 sm:py-10">
                    <ul className="space-y-4">
                      {BENEFITS.map((benefit) => (
                        <li key={benefit} className="flex items-start">
                          <div className="flex-shrink-0">
                            <CheckIcon />
                          </div>
                          <p className="ml-3 text-base leading-6 font-medium text-gray-500">
                            {benefit}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-10">
                      <div className="rounded-lg shadow-md">
                        <Link
                          href="/login"
                          aria-describedby="tier-pro-annual"
                          className="block w-full text-center rounded-lg border border-transparent bg-blue-800 px-6 py-4 text-xl leading-6 font-medium text-white hover:bg-blue-900 focus:outline-none focus:border-blue-700 transition ease-in-out duration-150"
                        >
                          Start your 14-day free trial
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-8 text-center text-base text-gray-500">
          14-day free trial. Cancel anytime.
        </p>
      </div>
    </MarketingLayout>
  );
}
